import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { initEmbed, initEmbedAutoResize } from "@/lib/embed";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute, AdminRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/admin/AdminLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import Booking from "@/pages/Booking";
import MyBookings from "@/pages/MyBookings";
import Membership from "@/pages/Membership";
import MembershipSuccess from "@/pages/MembershipSuccess";
import BookingSuccess from "@/pages/BookingSuccess";
import Tournaments from "@/pages/Tournaments";
import Rewards from "@/pages/Rewards";
import Leaderboards from "@/pages/Leaderboards";
import Community from "@/pages/Community";
import InviteAccept from "@/pages/InviteAccept";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import BookGuest from "@/pages/BookGuest";
import PreviewTournaments from "@/pages/PreviewTournaments";
import PreviewLeaderboards from "@/pages/PreviewLeaderboards";
import Pair from "@/pages/Pair";
import MySessions from "@/pages/MySessions";

import AdminOverview from "@/pages/admin/Overview";
import AdminUsers from "@/pages/admin/Users";
import AdminStations from "@/pages/admin/Stations";
import AdminBookings from "@/pages/admin/Bookings";
import AdminTournaments from "@/pages/admin/Tournaments";
import AdminTournamentDetail from "@/pages/admin/TournamentDetail";
import AdminMemberships from "@/pages/admin/Memberships";
import AdminRewards from "@/pages/admin/Rewards";
import AdminNotifications from "@/pages/admin/Notifications";
import AdminDataExport from "@/pages/admin/DataExport";
import AdminSimSessions from "@/pages/admin/SimSessions";
import AdminChannels from "@/pages/admin/Channels";
import AdminReviews from "@/pages/admin/Reviews";
import AdminEmbeds from "@/pages/admin/Embeds";
import AdminSimHub from "@/pages/admin/SimHub";
import AdminLeaderboardTracks from "@/pages/admin/LeaderboardTracks";
import AdminGiftCardRequests from "@/pages/admin/GiftCardRequests";
import AdminWaiver from "@/pages/admin/Waiver";
import AdminWaiverSignatures from "@/pages/admin/WaiverSignatures";
import AdminStationsLive from "@/pages/admin/StationsLive";
import AdminQrCodes from "@/pages/admin/QrCodes";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminCarClubs from "@/pages/admin/CarClubs";
import CarClubs from "@/pages/CarClubs";
import Checkin from "@/pages/Checkin";
import StationCheckin from "@/pages/StationCheckin";
import ReviewSubmit from "@/pages/ReviewSubmit";
import PublicReviews from "@/pages/PublicReviews";
import EmbedReviews from "@/pages/EmbedReviews";
import PublicBooking from "@/pages/PublicBooking";

const Protected = ({ children }) => (
  <ProtectedRoute>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

const Admin = ({ children }) => (
  <AdminRoute>
    <AdminLayout>{children}</AdminLayout>
  </AdminRoute>
);

function App() {
  useEffect(() => {
    initEmbed();
    initEmbedAutoResize();
  }, []);
  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "rgba(14,18,34,0.95)",
              border: "1px solid rgba(0,229,255,0.3)",
              color: "#eaf2ff",
              backdropFilter: "blur(12px)",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/verify" element={<VerifyEmail />} />
          <Route path="/book" element={<BookGuest />} />
          <Route path="/preview/tournaments" element={<PreviewTournaments />} />
          <Route path="/preview/leaderboards" element={<PreviewLeaderboards />} />
          <Route path="/pair/:code" element={<Pair />} />
          <Route path="/invite/:code" element={<InviteAccept />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/booking/:bookingId" element={<PublicBooking />} />
          <Route path="/reviews" element={<PublicReviews />} />
          <Route path="/embed/reviews" element={<EmbedReviews />} />
          <Route path="/checkin" element={<Checkin />} />
          <Route path="/checkin/station/:stationId" element={<StationCheckin />} />

          <Route path="/membership-success" element={<ProtectedRoute><MembershipSuccess /></ProtectedRoute>} />

          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/booking" element={<Protected><Booking /></Protected>} />
          <Route path="/my-bookings" element={<Protected><MyBookings /></Protected>} />
          <Route path="/sessions" element={<Protected><MySessions /></Protected>} />
          <Route path="/bookings" element={<Navigate to="/my-bookings" replace />} />
          <Route path="/membership" element={<Protected><Membership /></Protected>} />
          <Route path="/tournaments" element={<Protected><Tournaments /></Protected>} />
          <Route path="/rewards" element={<Protected><Rewards /></Protected>} />
          <Route path="/leaderboards" element={<Protected><Leaderboards /></Protected>} />
          <Route path="/car-clubs" element={<Protected><CarClubs /></Protected>} />
          <Route path="/community" element={<Protected><Community /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/review/:bookingId" element={<Protected><ReviewSubmit /></Protected>} />

          <Route path="/admin" element={<Admin><AdminOverview /></Admin>} />
          <Route path="/admin/users" element={<Admin><AdminUsers /></Admin>} />
          <Route path="/admin/stations" element={<Admin><AdminStations /></Admin>} />
          <Route path="/admin/bookings" element={<Admin><AdminBookings /></Admin>} />
          <Route path="/admin/tournaments" element={<Admin><AdminTournaments /></Admin>} />
          <Route path="/admin/tournaments/:id" element={<Admin><AdminTournamentDetail /></Admin>} />
          <Route path="/admin/memberships" element={<Admin><AdminMemberships /></Admin>} />
          <Route path="/admin/rewards" element={<Admin><AdminRewards /></Admin>} />
          <Route path="/admin/notifications" element={<Admin><AdminNotifications /></Admin>} />
          <Route path="/admin/data-export" element={<Admin><AdminDataExport /></Admin>} />
          <Route path="/admin/sim-sessions" element={<Admin><AdminSimSessions /></Admin>} />
          <Route path="/admin/channels" element={<Admin><AdminChannels /></Admin>} />
          <Route path="/admin/reviews" element={<Admin><AdminReviews /></Admin>} />
          <Route path="/admin/embeds" element={<Admin><AdminEmbeds /></Admin>} />
          <Route path="/admin/simhub" element={<Admin><AdminSimHub /></Admin>} />
          <Route path="/admin/leaderboard-tracks" element={<Admin><AdminLeaderboardTracks /></Admin>} />
          <Route path="/admin/gift-card-requests" element={<Admin><AdminGiftCardRequests /></Admin>} />
          <Route path="/admin/waiver" element={<Admin><AdminWaiver /></Admin>} />
          <Route path="/admin/waiver/signatures" element={<Admin><AdminWaiverSignatures /></Admin>} />
          <Route path="/admin/stations-live" element={<Admin><AdminStationsLive /></Admin>} />
          <Route path="/admin/qr-codes" element={<Admin><AdminQrCodes /></Admin>} />
          <Route path="/admin/audit-log" element={<Admin><AdminAuditLog /></Admin>} />
          <Route path="/admin/car-clubs" element={<Admin><AdminCarClubs /></Admin>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
